import { useCallback, useEffect, useRef, useState } from 'react'

import { QueryClient, useQueryClient } from '@tanstack/react-query'

import { AppTheme } from '@utils/theme'

export type UseAppTheme = () => AppTheme

type GetAppTheme = (queryClient: QueryClient) => AppTheme

const getAppTheme: GetAppTheme = (queryClient) =>
  queryClient.getQueryData<AppTheme>(['theme', 'app']) as AppTheme

const useAppTheme: UseAppTheme = () => {
  const queryClient = useQueryClient()
  const queryCache = queryClient.getQueryCache()
  const unsubscribeFnRef = useRef<() => void>(undefined)

  const [appTheme, setAppTheme] = useState<AppTheme>(() =>
    getAppTheme(queryClient)
  )

  const updateType = useCallback(
    (args: any) => {
      if (
        ['invalidate'].includes(args?.action?.type) &&
        args.query.queryKey[0] === 'theme' &&
        args.query.queryKey[1] === 'app'
      ) {
        setAppTheme(getAppTheme(queryClient))
      }
    },
    [queryClient]
  )

  useEffect(() => {
    setAppTheme(getAppTheme(queryClient))

    unsubscribeFnRef.current = queryCache.subscribe(updateType)

    return () => {
      if (unsubscribeFnRef.current) {
        unsubscribeFnRef.current()
      }
    }
  }, [queryCache, queryClient, updateType])

  return appTheme
}

export { getAppTheme }

export default useAppTheme
